import type { IGSplatHandleBinding } from '@/plugins/gsplat/GSplatHandleBinding'
import type { GSplatSystemAssetSnapshot } from '@/plugins/gsplat/GSplatSystem'
import type { GaussianSplatLoadRequest } from '@/plugins/gsplat/types/PluginTypes'
import type { AssetId } from '@/shared/types/core'

/** GSplatSystem 内部保存的资产记录；GPU payload 仍由 Resource lease 持有。 */
export interface GSplatAssetRecord {
  request: Readonly<GaussianSplatLoadRequest>
  readonly binding: IGSplatHandleBinding
}

/** 一次可取消加载的稳定 identity，用于阻止迟到结果提交。 */
export interface GSplatPendingLoad {
  readonly id: AssetId
  readonly generation: number
  readonly controller: AbortController
  readonly request: Readonly<GaussianSplatLoadRequest>
}

/**
 * 共享 3DGS 资产仓库。
 *
 * 对应旧 ModelRegistry 的职责，但只保存共享 Handle/Resource binding 和加载 identity；它不知道
 * graphics backend、URL 解析方式、renderer 或 picker。
 */
export class GSplatAssetStore {
  private readonly assets = new Map<AssetId, GSplatAssetRecord>()
  private readonly pendingLoads = new Map<AssetId, GSplatPendingLoad>()
  private nextGeneration = 1
  private closed = false

  allocateGeneration(): number {
    return this.nextGeneration++
  }

  has(id: AssetId): boolean {
    return this.assets.has(id)
  }

  isReserved(id: AssetId): boolean {
    return this.assets.has(id) || this.pendingLoads.has(id)
  }

  get(id: AssetId): GSplatAssetRecord | undefined {
    return this.assets.get(id)
  }

  set(id: AssetId, record: GSplatAssetRecord): void {
    this.assertOpen()
    this.assets.set(id, record)
  }

  delete(id: AssetId): GSplatAssetRecord | undefined {
    const record = this.assets.get(id)
    this.assets.delete(id)
    return record
  }

  values(): GSplatAssetRecord[] {
    return Array.from(this.assets.values())
  }

  getByUrl(url: string): GSplatAssetRecord | undefined {
    for (const record of this.assets.values()) {
      if (record.request.url === url) return record
    }
    return undefined
  }

  beginPending(
    id: AssetId,
    request: Readonly<GaussianSplatLoadRequest>,
    generation: number,
    replace = false
  ): GSplatPendingLoad {
    this.assertOpen()
    if (replace) this.abortPending(id)
    else if (this.pendingLoads.has(id)) {
      throw new Error(`GSplatAssetStore: asset id "${id}" 已在加载`)
    }
    const pending = Object.freeze({
      id,
      generation,
      controller: new AbortController(),
      request,
    })
    this.pendingLoads.set(id, pending)
    return pending
  }

  getPending(id: AssetId): GSplatPendingLoad | undefined {
    return this.pendingLoads.get(id)
  }

  isLoading(url: string): boolean {
    for (const pending of this.pendingLoads.values()) {
      if (pending.request.url === url) return true
    }
    return false
  }

  isCurrent(pending: GSplatPendingLoad): boolean {
    return (
      !this.closed &&
      !pending.controller.signal.aborted &&
      this.pendingLoads.get(pending.id) === pending
    )
  }

  finishPending(pending: GSplatPendingLoad): void {
    if (this.pendingLoads.get(pending.id) === pending) this.pendingLoads.delete(pending.id)
  }

  abortPending(id: AssetId): void {
    this.pendingLoads.get(id)?.controller.abort()
  }

  snapshot(): readonly GSplatSystemAssetSnapshot[] {
    return Object.freeze(
      Array.from(this.assets.entries(), ([id, record]) =>
        Object.freeze({
          id,
          generation: record.binding.generation,
          handle: record.binding.handle,
          resource: record.binding.resource,
          mounted: record.binding.mounted,
        })
      )
    )
  }

  close(): void {
    if (this.closed) return
    this.closed = true
    for (const pending of this.pendingLoads.values()) pending.controller.abort()
    this.pendingLoads.clear()
  }

  clear(): void {
    this.assets.clear()
  }

  private assertOpen(): void {
    if (this.closed) throw new Error('GSplatAssetStore 已关闭')
  }
}
